import { Link } from "react-router-dom";
import { Check, ArrowRight, MapPin, Phone, Globe, Star, Shield, Zap } from "lucide-react";
import { Button, Badge } from "@/components/kit";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import SiteFooter from "@/components/SiteFooter";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

const FEATURES = [
  { icon: MapPin, title: "Cari by Keyword & Lokasi", desc: "Ketik keyword bisnis (mis. 'klinik gigi') dan pilih kota atau radius. Data muncul dalam hitungan detik." },
  { icon: Phone, title: "Data Lengkap", desc: "Nama bisnis, telepon, email, website, rating, alamat, jam operasional — semua dalam satu tabel." },
  { icon: Globe, title: "Multi-Kota", desc: "Scrape dari Jakarta, Surabaya, Bandung, kota mana saja di Indonesia. Tidak ada batasan lokasi." },
  { icon: Star, title: "Filter Kualitas", desc: "Filter by rating, hanya ambil yang punya nomor HP, atau skip bisnis yang sudah punya website." },
  { icon: Shield, title: "Anti-Block", desc: "Rate limiting otomatis, rotasi IP, dan cooldown cerdas agar akun Google Maps kamu aman." },
  { icon: Zap, title: "Export CSV/Excel", desc: "Langsung download data sebagai CSV atau Excel. Siap pakai untuk CRM atau Google Sheets." },
];

const STEPS = [
  { n: "1", t: "Masukkan Keyword", d: "Ketik jenis bisnis yang ingin dicari, misalnya 'klinik gigi' atau 'katering'." },
  { n: "2", t: "Pilih Lokasi", d: "Pilih kota, atau tentukan radius tertentu dari lokasi kamu." },
  { n: "3", t: "Scrape & Download", d: "Tekan tombol scrape. Dalam hitungan detik, data lengkap siap diunduh." },
];

export default function GoogleMapsScraperLanding() {
  return (
    <div className="bg-white text-slate-900 min-h-screen">
      <Seo
        title="Google Maps Scraper Indonesia — Cari Data Bisnis Gratis | RESADA"
        description="Scrape data bisnis dari Google Maps Indonesia: nama, telepon, email, alamat, rating. Cari by keyword & lokasi. Mulai gratis."
        path="/tools/google-maps-scraper"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          name: "RESADA Google Maps Scraper",
          applicationCategory: "BusinessApplication",
          operatingSystem: "Web",
          description: "Scrape data bisnis dari Google Maps Indonesia untuk lead generation.",
          url: "https://resada.id/tools/google-maps-scraper",
        }}
      />
      <header className="sticky top-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo to="/" iconSize={32} />
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-600">
            <Link to="/" className="hover:text-sky-600">Beranda</Link>
            <Link to="/tools/google-maps-scraper" className="text-sky-600">Google Maps Scraper</Link>
            <Link to="/tools/whatsapp-blast" className="hover:text-sky-600">WhatsApp Blast</Link>
            <Link to="/pricing" className="hover:text-sky-600">Harga</Link>
            <Link to="/blog" className="hover:text-sky-600">Blog</Link>
          </nav>
          <div className="hidden md:flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" size="sm">Masuk</Button></Link>
            <Link to="/register"><Button size="sm">Daftar Gratis</Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="absolute -top-20 right-0 w-[500px] h-[500px] bg-sky-200/40 rounded-full blur-3xl" />
        <div className="max-w-4xl mx-auto px-6 pt-20 pb-24 text-center relative">
          <Badge color="sky" className="mb-5">Google Maps Scraper</Badge>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold leading-tight">
            Scraping Data Bisnis <span className="text-sky-500">Google Maps</span> Indonesia
          </h1>
          <p className="text-lg text-slate-600 mt-6 max-w-2xl mx-auto">
            Cari data bisnis dari Google Maps — nama, telepon, email, alamat, rating — dalam hitungan detik.
            Mulai dengan 50 lead gratis.
          </p>
          <div className="flex flex-wrap gap-3 mt-8 justify-center">
            <Link to="/register">
              <Button size="lg">Coba Gratis <ArrowRight className="w-4 h-4" /></Button>
            </Link>
            <Link to="/#cara">
              <Button size="lg" variant="outline">Lihat Cara Kerja</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <Badge color="sky" className="mb-4">Fitur</Badge>
          <h2 className="font-display text-4xl font-extrabold">Semua yang Kamu Butuh dari Google Maps</h2>
          <p className="text-slate-600 mt-4">Data bisnis lengkap, real-time, tanpa coding.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {FEATURES.map((f, i) => (
            <div key={f.title} className="card-soft p-7 hover:-translate-y-1 transition-transform">
              <div className="w-12 h-12 rounded-2xl bg-sky-50 grid place-items-center mb-5">
                <f.icon className="w-6 h-6 text-sky-600" />
              </div>
              <h3 className="font-display text-xl font-bold">{f.title}</h3>
              <p className="text-slate-600 mt-2 text-sm leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="bg-slate-900 text-white py-24 relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="max-w-5xl mx-auto px-6 relative">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Badge color="sky" className="mb-4">Cara Kerja</Badge>
            <h2 className="font-display text-4xl font-extrabold">3 Langkah Saja</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {STEPS.map(s => (
              <div key={s.n} className="text-center">
                <span className="inline-flex w-14 h-14 rounded-full bg-sky-500/20 text-sky-400 font-display text-2xl font-extrabold grid place-items-center">{s.n}</span>
                <h3 className="font-display text-xl font-bold mt-4">{s.t}</h3>
                <p className="text-slate-400 mt-2">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-20 text-center">
        <h2 className="font-display text-3xl font-extrabold">Siap mulai scrape Google Maps?</h2>
        <p className="text-slate-600 mt-3">50 leads gratis, tanpa kartu kredit.</p>
        <Link to="/register" className="inline-block mt-6">
          <Button size="lg">Daftar Gratis Sekarang <ArrowRight className="w-4 h-4" /></Button>
        </Link>
      </section>

      <FloatingWhatsApp />
      <SiteFooter />
    </div>
  );
}
